import { faPortrait } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef, useEffect, useState } from 'react';
import style from './AdminRankResponse.style';
import UserPhoto from '../../../../../../DragAndDropFiles/UserPhoto/UserPhoto';
import { Card, CardBody, CardHeader, UncontrolledTooltip } from 'reactstrap';
import { questionTypes } from '../constants';
import { popoverModifiers } from '../../../../../../../enums';

/**
 * Helper component to render rating bars with tooltip
 * Uses ref to ensure target exists before rendering tooltip
 */
const RatingWithTooltip = ({ tooltipId, description, evaluatorRating, renderRatingBars }) => {
  const targetRef = useRef(null);
  const [targetReady, setTargetReady] = useState(false);

  useEffect(() => {
    if (targetRef.current) {
      setTargetReady(true);
    }
  }, []);

  return (
    <>
      <div
        ref={targetRef}
        id={tooltipId}
        style={{
          ...style.simpleRow,
          verticalAlign: 'middle',
          cursor: description ? 'help' : 'default',
        }}
      >
        {renderRatingBars(evaluatorRating)}
      </div>
      {description && targetReady && (
        <UncontrolledTooltip target={tooltipId} placement="top" modifiers={popoverModifiers}>
          {description}
        </UncontrolledTooltip>
      )}
    </>
  );
};

/**
 * @param {string} filterType comment, questionRating
 * @param {string} displayType mini, expanded
 * @param {array} questionScale CustomScale array with Label and Description for each answer option
 */
const AdminRankResponse = ({ item = {}, filterType, displayType = 'mini', questionScale = null }) => {
  const { index } = item;

  // Ref and state for tooltip target readiness
  const tooltipTargetRef = useRef(null);
  const [tooltipTargetReady, setTooltipTargetReady] = useState(false);

  // Check if tooltip target is ready
  useEffect(() => {
    if (tooltipTargetRef.current) {
      setTooltipTargetReady(true);
    }
  }, []);

  // Helper function to get the description for a given answer number
  const getDescriptionForAnswer = (answerNumber) => {
    if (!questionScale || !Array.isArray(questionScale)) {
      return null;
    }

    const scaleItem = questionScale.find((scale) => {
      return scale.DisplayValue === answerNumber || scale.NumericValue === answerNumber;
    });

    if (!scaleItem) {
      return '';
    }
    return `${
      scaleItem.Label ? `${scaleItem.Label}${scaleItem.Description ? ' - ' : ''}` : ''
    } ${scaleItem.Description || ''}`;
  };

  const renderRatingBars = (evaluator) => {
    const { AnswerNumber = 0, MaxAnswerValue = 5 } = evaluator;
    const bars = [];
    let count = 0;
    while (count < MaxAnswerValue) {
      const barColor = count <= AnswerNumber - 1 ? '#118ded' : '#606b63';
      const width = `${(1 / MaxAnswerValue) * 100}%`;
      bars.push(<div style={{ ...style.ratingBar, backgroundColor: barColor, width }} />);
      count++;
    }
    return bars;
  };

  if (displayType === 'mini') {
    const backgroundColor = index % 2 == 0 ? 'var(--rr-surface-stripe)' : 'var(--rr-surface)';
    const {
      UserFirst,
      UserLast,
      UserPhotoUrl,
      AnswerNumber,
      AnswerText,
      MaxAnswerValue = 5,
      questionType = questionTypes.Rating,
      AnswerTypeNumber1OrText2,
    } = item;

    if (AnswerTypeNumber1OrText2 == 2) {
      return (
        <div
          style={{ ...style.simpleRow, backgroundColor, paddingTop: 10, paddingBottom: 10, verticalAlign: 'middle' }}
        >
          <div style={{ ...style.simpleColumn, width: '20%' }}>
            <div
              style={{
                ...style.simpleRow,
                alignItems: 'center',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                paddingLeft: 10,
              }}
            >
              {UserPhotoUrl ? (
                <img
                  src={UserPhotoUrl}
                  style={{ objectFit: 'contain', borderRadius: 20, height: 60, width: 60 }}
                  alt="candidate headshot"
                />
              ) : (
                <FontAwesomeIcon icon={faPortrait} fontSize={30} />
              )}
            </div>
          </div>
          <div style={style.commentContainer}>
            <div style={style.evaluatorName}>
              {UserLast || ''}, {UserFirst || ''}
            </div>
            <div style={{ ...style.simpleRow, verticalAlign: 'middle' }}>{AnswerText}</div>
          </div>
        </div>
      );
    }
    if (AnswerTypeNumber1OrText2 == 1) {
      const description = getDescriptionForAnswer(AnswerNumber);
      // Create a safe ID without special characters
      const tooltipId = `response_tooltip_${index}_${Math.random()
        .toString(36)
        .substring(2, 11)}`;

      return (
        <div
          style={{ ...style.simpleRow, backgroundColor, paddingTop: 10, paddingBottom: 10, verticalAlign: 'middle' }}
        >
          <div style={{ ...style.simpleColumn, width: '20%' }}>
            <div
              style={{
                ...style.simpleRow,
                alignItems: 'center',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                paddingTop: 5,
                paddingBottom: 5,
                paddingLeft: 10,
              }}
            >
              {UserPhotoUrl ? (
                <img src={UserPhotoUrl} style={{ objectFit: 'contain', borderRadius: 20, height: 60, width: 60 }} />
              ) : (
                <FontAwesomeIcon icon={faPortrait} style={{ fontSize: 60 }} />
              )}
            </div>
          </div>
          <div style={style.commentContainer}>
            <div style={style.evaluatorName}>
              {UserLast || ''}, {UserFirst || ''} : {AnswerNumber || 0} / {MaxAnswerValue || 0}
            </div>
            <div
              ref={tooltipTargetRef}
              id={tooltipId}
              style={{ ...style.simpleRow, verticalAlign: 'middle', cursor: description ? 'help' : 'default' }}
            >
              {renderRatingBars(item)}
            </div>
            {description && tooltipTargetReady && (
              <UncontrolledTooltip target={tooltipId} placement="top" modifiers={popoverModifiers}>
                {description}
              </UncontrolledTooltip>
            )}
          </div>
        </div>
      );
    }
    return (
      <div style={{ ...style.simpleRow, backgroundColor, paddingTop: 10, paddingBottom: 10, verticalAlign: 'middle' }}>
        <div style={{ ...style.simpleColumn, width: '20%' }}>
          <div
            style={{
              ...style.simpleRow,
              alignItems: 'center',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              paddingTop: 5,
              paddingBottom: 5,
              paddingLeft: 10,
            }}
          >
            {UserPhotoUrl ? (
              <img src={UserPhotoUrl} style={{ objectFit: 'contain', borderRadius: 20, height: 60, width: 60 }} />
            ) : (
              <FontAwesomeIcon icon={faPortrait} style={{ fontSize: 60 }} />
            )}
          </div>
        </div>
        <div style={style.commentContainer}>
          <div style={style.evaluatorName}>
            {UserLast || ''}, {UserFirst || ''}
          </div>
          <div style={{ ...style.simpleRow, verticalAlign: 'middle' }}>Unknown Response type!</div>
        </div>
      </div>
    );
  } else {
    const { Answers, index, QuestionText, qID, questionType } = item;

    return (
      <Card style={{ marginTop: 20 }}>
        <CardHeader>
          <b>{`#${index + 1}.`}</b>
          {` ${QuestionText}`}
        </CardHeader>
        <CardBody>
          {Answers.length > 0 ? (
            Answers.map((evaluatorRating, i) => {
              const {
                UserFirst,
                UserLast,
                UserPhotoUrl,
                AnswerText,
                AnswerNumber,
                MaxAnswerValue = 5,
                AnswerTypeNumber1OrText2,
              } = evaluatorRating;

              const description = getDescriptionForAnswer(AnswerNumber);
              // Create a safe ID without special characters
              const tooltipId = `response_expanded_tooltip_${index}_${i}_${Math.random()
                .toString(36)
                .substring(2, 11)}`;

              return (
                <div
                  style={{
                    ...style.simpleRow,
                    //   backgroundColor,
                    paddingTop: 10,
                    paddingBottom: 10,
                    verticalAlign: 'middle',
                    borderBottom: i < Answers.length - 1 ? '1px solid #dbdbd9' : null,
                  }}
                >
                  <div style={{ ...style.simpleColumn, width: '20%' }}>
                    <div
                      style={{
                        ...style.simpleRow,
                        alignItems: 'center',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      {UserPhotoUrl ? (
                        <img
                          src={UserPhotoUrl}
                          style={{ objectFit: 'contain', borderRadius: 20, height: 60, width: 60 }}
                        />
                      ) : (
                        <FontAwesomeIcon icon={faPortrait} style={{ fontSize: 60 }} />
                      )}
                    </div>
                  </div>
                  <div style={style.commentContainer}>
                    <div style={style.evaluatorName}>
                      {UserLast || ''}, {UserFirst || ''}{' '}
                      {`${AnswerTypeNumber1OrText2 == 2 ? '' : `: ${AnswerNumber || 0} / ${MaxAnswerValue || 0}`}`}
                    </div>

                    {AnswerTypeNumber1OrText2 != 2 ? (
                      <RatingWithTooltip
                        tooltipId={tooltipId}
                        description={description}
                        evaluatorRating={evaluatorRating}
                        renderRatingBars={renderRatingBars}
                      />
                    ) : null}
                    {AnswerTypeNumber1OrText2 == 2 ? (
                      <div style={{ ...style.simpleRow, verticalAlign: 'middle' }}>
                        <div style={style.simpleColumn}>
                          <div style={{ textIndent: 40 }}>{AnswerText || 'No Comments.'}</div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          ) : (
            <div
              style={{
                ...style.simpleRow,
                //   backgroundColor,
                paddingTop: 10,
                paddingBottom: 10,
                verticalAlign: 'middle',
              }}
            >
              No responses recorded for this question for this candidate.
            </div>
          )}
        </CardBody>
      </Card>
    );
  }
};
export default AdminRankResponse;
